import React, { useState } from 'react';
import { Layers, Download, Info, LineChart } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения проекции пенсии по годам
 */
const PensionProjection = ({ projection, results, showFullProjection, setShowFullProjection }) => {
  const [activeTab, setActiveTab] = useState('income');
  
  if (!projection || projection.length === 0 || !results) {
    return null;
  }

  // Для отображения только первых 10 лет, если не показана полная проекция
  const displayProjection = showFullProjection ? projection : projection.slice(0, 10);

  // Определяем год выхода на пенсию
  const retirementAge = results.retirementAge;
  const retirementYear = projection.findIndex(item => item.age >= retirementAge);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <LineChart className="w-5 h-5 mr-2 text-emerald-500" />
          Прогноз пенсионных показателей
        </h3>

        {/* Кнопка переключения полной/краткой проекции */}
        {projection.length > 10 && (
          <Button
            size="sm"
            onClick={() => setShowFullProjection(prev => !prev)}
            variant="outline"
            aria-expanded={showFullProjection}
          >
            {showFullProjection ? 'Краткий прогноз' : `Показать все ${projection.length} лет`}
          </Button>
        )}
      </div>

      {/* Вкладки для разных типов отображения */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4 overflow-x-auto">
        <button
          onClick={() => setActiveTab('income')}
          className={`py-2 px-4 text-sm font-medium whitespace-nowrap rounded-t-lg ${
            activeTab === 'income'
              ? 'text-emerald-600 dark:text-emerald-400 border-b-2 border-emerald-500 dark:border-emerald-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Доходы и накопления
        </button>
        <button
          onClick={() => setActiveTab('pension')}
          className={`py-2 px-4 text-sm font-medium whitespace-nowrap rounded-t-lg ${
            activeTab === 'pension'
              ? 'text-emerald-600 dark:text-emerald-400 border-b-2 border-emerald-500 dark:border-emerald-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Расчет пенсии
        </button>
        <button
          onClick={() => setActiveTab('points')}
          className={`py-2 px-4 text-sm font-medium whitespace-nowrap rounded-t-lg ${
            activeTab === 'points'
              ? 'text-emerald-600 dark:text-emerald-400 border-b-2 border-emerald-500 dark:border-emerald-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Пенсионные баллы
        </button>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>
            {showFullProjection 
              ? "Полная проекция с учетом инфляции и изменения доходов. Прокрутите таблицу вправо для просмотра всех данных." 
              : `Показаны первые ${displayProjection.length} лет прогноза. Нажмите "Показать все" для просмотра полной проекции.`
            }
            {retirementYear > 0 && ` Вы выходите на пенсию в ${projection[retirementYear]?.year} году.`}
          </p>
        </div>
      </div>

      {/* Контейнер таблицы с прокруткой */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[600px] pension-projection-table">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[800px]">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
            <tr>
              <th scope="col" className="px-3 py-2 font-semibold text-center">Год</th>
              <th scope="col" className="px-3 py-2 font-semibold text-center">Возраст</th>

              {activeTab === 'income' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Ежемесячный доход</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Ежемесячный взнос</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Накопления</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Общая пенсия</th>
                </>
              )}

              {activeTab === 'pension' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Страховая пенсия</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Накопительная пенсия</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Региональная доплата</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Сельская надбавка</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Общая пенсия</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Коэффициент замещения</th>
                </>
              )}

              {activeTab === 'points' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Баллы за год</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Накопленные баллы</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Страховая пенсия</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Влияние инфляции</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {displayProjection.map((item, index) => {
              // Расчет коэффициента замещения (отношение пенсии к доходу до пенсии)
              const prevItemIncome = index > 0 ? displayProjection[index - 1].monthlyIncome : 0;
              const replacementRate = item.age >= retirementAge && prevItemIncome > 0 
                ? (item.totalPension / prevItemIncome) * 100 
                : 0;
              
              // Определяем, выходит ли человек на пенсию в этом году
              const isRetirementYear = item.age === retirementAge;
              
              return (
                <tr
                  key={item.year}
                  className={`
                    hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100
                    ${isRetirementYear ? 'bg-emerald-50 dark:bg-emerald-900/20 font-medium' : ''}
                    ${item.age >= retirementAge ? 'bg-emerald-50/50 dark:bg-emerald-900/10' : ''}
                  `}
                >
                  <td className="px-3 py-1.5 whitespace-nowrap text-center">{item.year}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-center">
                    {item.age}
                    {isRetirementYear && <span className="ml-1 text-emerald-600 dark:text-emerald-400 font-medium">🎉</span>}
                  </td>

                  {activeTab === 'income' && (
                    <>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right">
                        {item.monthlyIncome > 0 ? formatCurrency(item.monthlyIncome, 0) : '-'}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right">
                        {item.monthlySavingsContribution > 0 ? formatCurrency(item.monthlySavingsContribution, 0) : '-'}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">
                        {formatCurrency(item.cumulativeSavings, 0)}
                      </td>
                      <td className={`px-3 py-1.5 whitespace-nowrap text-right ${item.age >= retirementAge ? 'text-emerald-600 dark:text-emerald-400 font-semibold' : 'text-gray-400 dark:text-gray-500'}`}>
                        {item.age >= retirementAge ? formatCurrency(item.totalPension, 0) : '-'}
                      </td>
                    </>
                  )}

                  {activeTab === 'pension' && (
                    <>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right">
                        {item.age >= retirementAge ? formatCurrency(item.insurancePension, 0) : '-'}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right">
                        {item.age >= retirementAge && item.voluntaryPension > 0 ? formatCurrency(item.voluntaryPension, 0) : '-'}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right">
                        {item.age >= retirementAge && item.regionalBonus > 0 ? formatCurrency(item.regionalBonus, 0) : '-'}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right">
                        {item.age >= retirementAge && item.ruralBonus > 0 ? formatCurrency(item.ruralBonus, 0) : '-'}
                      </td>
                      <td className={`px-3 py-1.5 whitespace-nowrap text-right ${item.age >= retirementAge ? 'text-emerald-600 dark:text-emerald-400 font-semibold' : 'text-gray-400 dark:text-gray-500'}`}>
                        {item.age >= retirementAge ? formatCurrency(item.totalPension, 0) : '-'}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right">
                        {item.age >= retirementAge && replacementRate > 0 ? `${formatNumber(replacementRate, 1)}%` : '-'}
                      </td>
                    </>
                  )}

                  {activeTab === 'points' && (
                    <>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right">
                        {item.yearIpk > 0 ? formatNumber(item.yearIpk, 2) : '-'}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">
                        {formatNumber(item.ipkAccumulated, 2)}
                      </td>
                      <td className={`px-3 py-1.5 whitespace-nowrap text-right ${item.age >= retirementAge ? 'text-emerald-600 dark:text-emerald-400 font-semibold' : 'text-gray-400 dark:text-gray-500'}`}>
                        {item.age >= retirementAge ? formatCurrency(item.insurancePension, 0) : '-'}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right text-orange-600 dark:text-orange-400">
                        x{item.inflationFactor > 0 ? formatNumber(item.inflationFactor, 2) : '-'}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Кнопка показа полной проекции (если она скрыта и больше предпросмотра) */}
      {!showFullProjection && projection.length > 10 && (
        <div className="text-center mt-4">
          <Button
            onClick={() => setShowFullProjection(true)}
            icon={<Layers className="w-4 h-4" />}
            variant="link"
          >
            Показать все {projection.length} лет прогноза
          </Button>
        </div>
      )}
    </div>
  );
};

export default PensionProjection;